import React, { Component, Fragment } from 'react';
import withStyles from '@mui/styles/withStyles';
import { withRouter } from '../../../util/withRouter';
import Checkbox from '../../../component/form/Checkbox';
import Grid from '../../../component/form/Grid';
import Label from '../../../component/form/Label';
import Select from '../../../component/form/Select';
import TextField from '../../../component/form/TextField';
import PrimaryButton from '../../../component/form/PrimaryButton';
import OptionalTextField from '../../../component/form/OptionalTextField';
import AutoComplete from '../../../component/form/AutoComplete';
import AddressAutocomplete from '../../../component/form/AddressAutocomplete';
import CustomAddressModal from '../../../component/form/CustomAddressModal';
import CoronerModal from '../../../component/form/CoronerModal';
import { ADDRESS_BOOK_CATEGORY } from '../funeralConstants';
import { InlineField, InlineHeader, InlineHeaderRightAlignChildren } from '../../../component/form/Inline';
import { ContactCard, ContactCardContainer } from '../../../component/form/ContactCard';
import ContactCardModal from '../../../component/form/ContactCardModal';
import PhoneNumberList, { listedPhones, matchesPhone } from '../../../component/form/PhoneNumberList';
import {
    APPLICANT_RELATIONSHIP_OPTIONS,
    CERTIFICATION_OF_DEATH,
    CORONERS_CAUSES,
    POSTAL_ADDRESS_COMPONENTS,
    RESIDENTIAL_ADDRESS_COMPONENTS
} from './CertificationConstants';
import CoronerAutoComplete from '../../../component/form/CoronerAutoComplete';
import ColumnLayout from '../../../component/ColumnLayout';
import { isRelatedObjectDefined } from '../../../util/bookable';
import { joinDefined, stringIsNullOrEmpty } from '../../../util/strings';
import PurchaseOrderAddressBookAutocomplete from '../../../component/bookings/PurchaseOrderAddressBookAutocomplete';
import { createCertificationPurchaseOrderMutation } from '../../../component/bookings/PurchaseOrderModal';
import ValidationPlaceholder from '../../../component/form/ValidationPlaceholder';
import { validationHelper } from '../../../util/validation';
import { compose } from '../../../reactApolloCompat';
import FamilyMemberAutocomplete from '../../../component/form/FamilyMemberAutocomplete';
import { getClient, getUtilitiesClient } from '../../../apollo';
import { getProducts } from '../../../util/products';
import { submitToXeroMutation } from '../billing/invoiceModal/GetSaveInvoice';
import { setSnackbarMessage } from '../../../context/SnackbarMessage';
import BiggerMessageBar from '../../../component/BiggerMessageBar';
import { ContactsIcon } from '../../../component/IconIndex';
import CertificationTable from './CertificationTable';
import AddressBookAutocomplete from '../../../component/form/AddressBookAutocomplete';
import DisplayContact from '../../../component/bookings/DisplayContact';

const informantPhoneValidation = form => {
    const changes = form.getField('CertificationChangesFromBDM') || {};
    const phones = form.getField('Informant.Phones');
    const unmatched = ['Informant.Mobile', 'Informant.Phone']
        .map(fieldName => changes[fieldName])
        .filter(value => !stringIsNullOrEmpty(value) && !matchesPhone(phones, value));

    if (unmatched.length > 0) {
        return validationHelper.optional('Family entered: ' + unmatched.join(' / '));
    }

    return listedPhones(phones).length > 0 ? validationHelper.ok() : validationHelper.suggested();
};

class Certification extends Component {
    state = {
        coronerModalField: undefined,
        authorizedContactModalField: undefined,
        editContact: undefined,
        ApplicantFamilyMember: ''
    };

    static getDerivedStateFromProps({ form }, oldState) {
        const InformantsName = form && form.getField('Informant.InformantsName');
        const RelationshipToDeceased = form && form.getField('Informant.RelationshipToDeceased');
        if (
            !!InformantsName &&
            !!RelationshipToDeceased &&
            ['Husband', 'Wife', 'Daughter', 'Son', 'Father', 'Mother', 'Widow', 'Widower', 'Child', 'Parent'].indexOf(
                RelationshipToDeceased
            ) > -1
        ) {
            const newMember = '' + InformantsName + ' (' + RelationshipToDeceased + ')';
            return { ApplicantFamilyMember: newMember };
        }

        return null;
    }

    onChangePostalAddress = e => {
        const { form } = this.props;
        const { target } = e;
        if (target) {
            if (!target.checked) return;
        } else if (!form.getField('Informant.PostalSameAsResidential')) return;

        const postalAddress = form.getField('Informant.ResidentialAddress1');
        const postalSuburb = form.getField('Informant.SuburbCityAust');
        const postalState = form.getField('Informant.ResidentialState');
        const postalPostcode = form.getField('Informant.ResidentialPostcode');

        form.setField({
            'Informant.PostalAddressLine1': postalAddress,
            'Informant.PostalSuburb': postalSuburb,
            'Informant.PostalState': postalState,
            'Informant.PostalPostcode': postalPostcode
        });
    };

    /* leaving this here in case they want to bring this functionality back */
    onChangeApplicantFamilyMember = item => {
        const { form } = this.props;
        let ApplicantFamilyMember = '';
        if (item && item.metadata) {
            const { source, relationship } = item.metadata;
            form.setField({
                'Informant.RelationshipToDeceased': relationship,
                'Informant.InformantsName': joinDefined([source.FirstName, source.MiddleName, source.Surname], ' '),
                'Informant.FirstName': source.FirstName,
                'Informant.MiddleName': source.MiddleName,
                'Informant.Surname': source.Surname
            });
            ApplicantFamilyMember = item.Label;
        } else {
            form.setField({
                'Informant.RelationshipToDeceased': null,
                'Informant.InformantsName': null,
                'Informant.FirstName': null,
                'Informant.MiddleName': null,
                'Informant.Surname': null
            });
        }
        this.setState({ ApplicantFamilyMember });
    };

    onOpenCoronerModal = name => {
        this.setState({ coronerModalField: name });
    };

    onCreateNewCoroner = coroner => {
        const { form } = this.props;
        form.setField({ 'Certification.CoronersCode': coroner.ID });
    };

    onSubmitContactCard = contact => {
        const { form } = this.props;
        const AuthorisedContacts = (form ? form.getField('AuthorisedContacts') : null) || [];
        const existingContact = contact.ID !== undefined ? AuthorisedContacts.find(x => x.ID === contact.ID) : null;

        if (existingContact) {
            Object.assign(existingContact, contact);
        } else {
            AuthorisedContacts.push(contact);
        }
        form.setField({ AuthorisedContacts });
        this.setState({ editContact: undefined });
    };

    render() {
        const { form } = this.props;
        const { coronerModalField, editContact, customAddressFields } = this.state;
        return (
            <Fragment>
                <CustomAddressModal
                    onClose={() => this.setState({ customAddressFields: undefined })}
                    open={!!customAddressFields}
                    name={customAddressFields}
                    form={form}
                    customAddressFields={customAddressFields}
                />
                <CoronerModal
                    onClose={() => this.setState({ coronerModalField: undefined })}
                    open={!!coronerModalField}
                    name={coronerModalField}
                    form={form}
                    onSubmit={this.onCreateNewCoroner}
                />
                <ContactCardModal
                    onClose={() => this.setState({ editContact: undefined })}
                    open={!!editContact}
                    value={editContact}
                    onSubmit={this.onSubmitContactCard}
                />
                <ColumnLayout>
                    {this.renderFirstColumn()}
                    {this.renderSecondColumn()}
                </ColumnLayout>
            </Fragment>
        );
    }

    renderFirstColumn() {
        const { form, locked } = this.props;
        const funeralId = form.getField('ID');
        const { ApplicantFamilyMember } = this.state;
        const isSigned = form.getField('Informant.Signed');
        return (
            <Grid container spacing={2}>
                <Grid bucket={true}>
                    <Grid pc={1}>
                        <Label text="Informant Identity (the Applicant)" />
                    </Grid>

                    {1 === 2 && (
                        <Grid pc={0.5}>
                            <FamilyMemberAutocomplete
                                name="ApplicantFamilyMember"
                                onChange={this.onChangeApplicantFamilyMember}
                                funeralId={funeralId}
                                value={ApplicantFamilyMember}
                            />
                        </Grid>
                    )}
                    {
                        /* !!ApplicantFamilyMember && */ <Fragment>
                            <Grid pc={1}>
                                <AutoComplete
                                    disabled={locked}
                                    name="Informant.RelationshipToDeceased"
                                    label="Relationship to the deceased"
                                    form={form}
                                    limit={100}
                                    options={APPLICANT_RELATIONSHIP_OPTIONS}
                                />
                            </Grid>

                            {/* <Grid pc={0.5}>
                        <TextField name="Informant.InformantsName" label="Full name" form={form} />
                    </Grid>*/}
                            <Grid pc={0.5}>
                                <TextField
                                    disabled={locked}
                                    titleCase
                                    name="Informant.FirstName"
                                    label="First name"
                                    form={form}
                                />
                            </Grid>
                            <Grid pc={0.5}>
                                <TextField
                                    disabled={locked}
                                    titleCase
                                    name="Informant.MiddleName"
                                    label="Middle name(s)"
                                    form={form}
                                />
                            </Grid>
                            <Grid pc={0.5}>
                                <TextField
                                    disabled={locked}
                                    titleCase
                                    name="Informant.Surname"
                                    label="Surname"
                                    form={form}
                                />
                            </Grid>
                        </Fragment>
                    }
                    <Grid pc={0.5}>
                        <TextField
                            disabled={locked}
                            name="Informant.Email"
                            label="Email (optional)"
                            type="email"
                            form={form}
                        />
                    </Grid>

                    <PhoneNumberList
                        disabled={locked}
                        name="Informant.Phones"
                        label="Phone"
                        form={form}
                        validationResult={informantPhoneValidation(form)}
                    />

                    <Grid pc={1}>
                        <InlineHeader header="Residential Address" />
                        <AddressAutocomplete
                            disabled={locked}
                            id="ResidentialAddress"
                            placeholder="Search for residential address"
                            componentFields={RESIDENTIAL_ADDRESS_COMPONENTS}
                            form={form}
                            allowCustomAddress={true}
                            onSelect={this.onChangePostalAddress}
                        />
                    </Grid>

                    <Grid pc={1}>
                        <InlineHeader header="Postal Address">
                            <Checkbox
                                disabled={locked}
                                name="Informant.PostalSameAsResidential"
                                label="Same as residential address"
                                form={form}
                                onChange={this.onChangePostalAddress}
                            />
                        </InlineHeader>
                        <AddressAutocomplete
                            id="PostalAddress"
                            placeholder="Search for postal address1"
                            componentFields={POSTAL_ADDRESS_COMPONENTS}
                            form={form}
                            allowCustomAddress={true}
                            disabled={form.getField('Informant.PostalSameAsResidential') || locked}
                        />
                    </Grid>
                    <Grid pc={1}>
                        <OptionalTextField
                            name="Informant.Comment"
                            buttonlabel="+ Add Informant Notes"
                            textfieldlabel={'Informant Notes'}
                            multiline
                            form={form}
                        />
                    </Grid>
                </Grid>
                <Grid bucket={true}>
                    <Grid pc={1}>
                        <Label text="Informant Declaration" />
                    </Grid>
                    <Grid pc={1}>
                        <Checkbox
                            disabled={locked}
                            name="Informant.Signed"
                            label="The informant has signed the declaration form"
                            form={form}
                        />
                    </Grid>
                    {isSigned && (
                        <Grid pc={0.5}>
                            <Label text="Date Signed" htmlFor="text-Informant.DateOfSignature" />
                            <TextField
                                disabled={locked}
                                name="Informant.DateOfSignature"
                                type="date"
                                notFuture
                                form={form}
                            />
                        </Grid>
                    )}
                </Grid>

                {this.renderAuthorisedContacts()}
            </Grid>
        );
    }

    renderSecondColumn() {
        const { form, locked } = this.props;

        let certificationFields;

        const certificateType = form.getField('Certification.TypeOfBdmDeathCertificate');

        if (certificateType === 'Coroner') {
            certificationFields = this.renderCoronerFields(form);
        } else {
            certificationFields = this.renderDoctorFields(form);
        }

        const isMccd = certificateType === 'MCCD';

        const isCremation = form.getField('Disposal.CrematedOrBuried') === 'Cremated';
        const isCoroner = certificateType === 'Coroner';

        const hasMortuary = form.getField('MortuaryLocation.ID');
        const hasTransfer = form.getField('TransferActioned');
        const canUpdateMortuary = hasTransfer && form.isDirtyField('MortuaryLocation.ID');

        return (
            <Grid container spacing={2}>
                <Grid bucket={true}>
                    <Grid pc={1}>
                        <Label disabled={locked} text="Certification of Death" htmlFor="select-CertificationOfDeath" />
                    </Grid>

                    <Grid pc={1}>
                        <Select
                            disabled={locked}
                            allowNone={false}
                            name="Certification.TypeOfBdmDeathCertificate"
                            placeholder="Select a Certification of Death"
                            options={CERTIFICATION_OF_DEATH}
                            form={form}
                        />
                    </Grid>
                    {certificationFields}
                    {isCremation && !isCoroner && this.renderRefereeFields(form, isMccd && isCremation)}
                    <Grid pc={1}>
                        <OptionalTextField
                            name="DoctorCoronerComment"
                            buttonlabel="+ Add Certification Notes"
                            textfieldlabel={'Notes from the Coroner/Doctors...'}
                            form={form}
                        />
                    </Grid>
                    {isCremation && (
                        <Grid item xs={12}>
                            <Checkbox
                                disabled={locked}
                                label="Pacemaker to be removed"
                                form={form}
                                name="RemovePacemaker"
                                onChange={(evt, val) => form.setField({ RemovePacemakerPending: !!val })}
                            />
                        </Grid>
                    )}
                </Grid>

                {this.renderRequiredDocumentation()}

                <Grid bucket={true}>
                    <InlineHeader header="Transfer">
                        <Checkbox name="TransferCertificates" label="Certificates received" form={form} />
                        <Checkbox name="TransferReady" label="Ready to collect" form={form} disabled={hasTransfer} />
                        <Checkbox name="TransferActioned" label="Inbound actioned" form={form} disabled />
                    </InlineHeader>

                    <Grid pc={1}>
                        <OptionalTextField
                            name="TransferComment"
                            form={form}
                            multiline
                            buttonlabel="+ Add Transfer Notes"
                            placeholder="Enter any notes..."
                        />
                    </Grid>

                    <Grid pc={1}>
                        {hasMortuary > 0 && !canUpdateMortuary ? (
                            <>
                                <InlineHeaderRightAlignChildren header="Mortuary Accommodation" />
                                <DisplayContact contactID={hasMortuary} />
                            </>
                        ) : (
                            hasTransfer && (
                                <AddressBookAutocomplete
                                    label="Mortuary Accommodation"
                                    name="MortuaryLocation"
                                    categories={[ADDRESS_BOOK_CATEGORY.mortuary]}
                                    form={form}
                                />
                            )
                        )}
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    renderDoctorFields(form) {
        const { locked } = this.props;
        const isCremation = form.getField('Disposal.CrematedOrBuried') === 'Cremated';
        return (
            <Fragment>
                <PurchaseOrderAddressBookAutocomplete
                    disabled={locked}
                    form={form}
                    placeholder="Search for doctor"
                    labelField="Name"
                    dataObjectType={'Certification'}
                    contactField="Doctor"
                    purchaseOrderField="DoctorPurchaseOrder"
                    categories={[ADDRESS_BOOK_CATEGORY.doctor]}
                    labelSupplier={'Doctor'}
                    allowPurchaseOrder={form.getField('DoctorAttachedToFlag') !== true && !!isCremation}
                    createPurchaseOrderMutation={createCertificationPurchaseOrderMutation}
                    defaultLineItemCode={'21930'}
                />

                <Grid pc={1}>
                    <Checkbox
                        disabled={locked}
                        name="DoctorAttachedToFlag"
                        label="Hospital attached to doctor"
                        form={form}
                    />
                </Grid>

                {form.getField('DoctorAttachedToFlag') && (
                    <PurchaseOrderAddressBookAutocomplete
                        disabled={locked}
                        form={form}
                        placeholder="Search for a hospital"
                        dataObjectType={'Certification'}
                        contactField="Hospital"
                        purchaseOrderField="HospitalPurchaseOrder"
                        categories={[ADDRESS_BOOK_CATEGORY.hospitalNursingHome]}
                        labelSupplier={'Hospital'}
                        allowPurchaseOrder={form.getField('DoctorAttachedToFlag') === true}
                        createPurchaseOrderMutation={createCertificationPurchaseOrderMutation}
                        defaultLineItemCode={'21910'}
                    />
                )}
            </Fragment>
        );
    }

    renderCoronerFields(form) {
        const { locked } = this.props;
        return (
            <Fragment>
                <Grid pc={1}>
                    <InlineField>
                        <Label text="Select a Coroner" />
                    </InlineField>
                    <InlineField>
                        <CoronerAutoComplete
                            disabled={locked}
                            name="Certification.Coroner"
                            placeholder="Search for coroner"
                            form={form}
                            addNewOnClick={!locked && (() => this.onOpenCoronerModal('Certification.Coroner'))}
                        />
                    </InlineField>
                </Grid>
                <Grid pc={1}>
                    <Select
                        disabled={locked}
                        name="Certification.CoronersCause"
                        label="Coroner's cause"
                        placeholder="Select a Coroner's Cause"
                        options={CORONERS_CAUSES}
                        form={form}
                    />
                </Grid>
            </Fragment>
        );
    }

    autobillReferee(form) {
        const client = getClient();
        const clientForProducts = getUtilitiesClient();
        const orderID = form.getField('Certification.RefereePurchaseOrder.ID');
        const certificationID = form.getField('Certification.ID');
        const funeralID = form.getField('ID');
        const supplierID = form.getField('Certification.Referee.ID');
        if (orderID > 0) return false; //We already have order, doing nothing
        if (this.props.locked) return false; //Funeral is locked, no autobills for you :D
        if (certificationID > 0 && funeralID > 0 && supplierID > 0) {
            //We have everything we need, let's create our bill
            this.autobillingInProgress = true;
            setSnackbarMessage('Creating bill for referee doctor', true);
            form.loading = true;
            this.invoiceCreationInProgress = true;
            clientForProducts
                .query({ query: getProducts, variables: { categoryUrlsegment: null, searchTerm: '2194' } })
                .then(product => {
                    const product_node = product.data.readProducts.edges[0] || {};
                    if (product_node.node.ID > 0) {
                        //Got one, now let's create actual order
                        client
                            .mutate({
                                mutation: createCertificationPurchaseOrderMutation,
                                variables: {
                                    input: {
                                        ID: certificationID,
                                        RefereePurchaseOrder: {
                                            FuneralID: funeralID,
                                            SupplierID: supplierID,
                                            Products: [
                                                {
                                                    ProductID: product_node.node.ID,
                                                    Qty: 1,
                                                    UnitPrice: Number(product_node.node.PurchasePrice)
                                                }
                                            ]
                                        }
                                    }
                                }
                            })
                            .then(order => {
                                const orderID = order.data.updateFuneralCertification.RefereePurchaseOrder.ID;
                                if (orderID > 0) {
                                    //Order created, submiting to Xero
                                    client
                                        .mutate({
                                            mutation: submitToXeroMutation,
                                            variables: {
                                                id: orderID
                                            }
                                        })
                                        .then(invoice => {
                                            this.autobillProcedureStopped(
                                                'Bill for referee doctor created successfully',
                                                true,
                                                form
                                            );
                                        })
                                        .catch(error => {
                                            this.autobillProcedureStopped(error.message, false, form);
                                        });
                                } else {
                                    throw new Error('Failed to submit to Xero');
                                }
                            })
                            .catch(error => {
                                this.autobillProcedureStopped(error.message, false, form);
                            });
                    } else {
                        throw new Error('Failed to found product to use in bill');
                    }
                })
                .catch(error => {
                    this.autobillProcedureStopped(error.message, false, form);
                });
            return true; //Well, at least we started here, will we be successful - we'll see
        }
        return false; //We missing some piece of required data, so just let's relax ;)
    }

    autobillProcedureStopped(message, success, form) {
        this.autobillingInProgress = false;
        setSnackbarMessage(
            !success ? 'Failed to create bill for referee doctor. Reason: ' + message : message,
            success
        );
        form.loading = false;
        form.doRefetch(() => null);
    }

    renderRefereeFields(form, allowPurchaseOrder) {
        const { locked } = this.props;
        let RefereeCremationCheck = form.getField('RefereeCremationCheck');
        if (
            !!form.getField('Certification.FileCremCertificate.AbsoluteLink') &&
            !!form.getField('Certification.FileRefereeingDoctorForm.AbsoluteLink')
        ) {
            if (!RefereeCremationCheck) {
                RefereeCremationCheck = true;
                form.setField({ RefereeCremationCheck });
            }
        } else {
            if (!!RefereeCremationCheck) {
                RefereeCremationCheck = false;
                form.setField({ RefereeCremationCheck });
            }
        }

        if (
            RefereeCremationCheck &&
            allowPurchaseOrder &&
            !this.invoiceCreationInProgress &&
            !form.isDirty &&
            !form.loading
        ) {
            this.autobillReferee(form);
        }

        return (
            <Fragment>
                {this.autobillingInProgress && (
                    <BiggerMessageBar messageType="loading">Referee doctor bill is being created</BiggerMessageBar>
                )}
                <PurchaseOrderAddressBookAutocomplete
                    disabled={locked}
                    form={form}
                    dataObjectType={'Certification'}
                    contactField="Referee"
                    purchaseOrderField="RefereePurchaseOrder"
                    categories={[ADDRESS_BOOK_CATEGORY.doctor]}
                    labelSupplier={'Referee Doctor'}
                    allowPurchaseOrder={false}
                    createPurchaseOrderMutation={createCertificationPurchaseOrderMutation}
                    defaultLineItemCode={'21940'}
                />

                <Grid pc={1}>
                    <Label>
                        {RefereeCremationCheck
                            ? 'Cremation Referee completed'
                            : 'Upload required documents to complete cremation referee'}
                    </Label>
                </Grid>
            </Fragment>
        );
    }

    renderAuthorisedContacts() {
        const { form, locked } = this.props;
        const AuthorisedContacts = form.getField('AuthorisedContacts') || [];
        return (
            <Grid bucket={true}>
                <Grid pc={1}>
                    <Label text={'Authorised Contacts'} />

                    {!!AuthorisedContacts.length ? (
                        <ContactCardContainer>
                            {AuthorisedContacts.map((obj, i) => (
                                <ContactCard
                                    contact={obj}
                                    key={i}
                                    onDelete={!locked && (() => this.onDeleteContact(i))}
                                    onEdit={
                                        !locked && isRelatedObjectDefined(obj) && (() => this.onEditContact(i, obj))
                                    }
                                />
                            ))}
                        </ContactCardContainer>
                    ) : (
                        <p style={{ marginTop: 6 }}>There are no authorised contacts.</p>
                    )}

                    <PrimaryButton forModal disabled={locked} onClick={() => this.onEditContact(undefined, undefined)}>
                        <ContactsIcon />
                        Add authorised contact...
                    </PrimaryButton>
                </Grid>
            </Grid>
        );
    }

    renderRequiredDocumentation() {
        const { form } = this.props;
        return (
            <Grid bucket={true}>
                <Grid pc={1}>
                    <Label text="Required Documentation" />
                </Grid>
                <ValidationPlaceholder validationResult={form.getValidation('Certification', true)}>
                    <CertificationTable form={form} />
                </ValidationPlaceholder>
            </Grid>
        );
    }

    onDeleteContact = index => {
        const { form } = this.props;
        const AuthorisedContacts = (form ? form.getField('AuthorisedContacts') : null) || [];

        AuthorisedContacts.splice(index, 1);

        form.setField({ AuthorisedContacts });
    };

    onEditContact = (index, contact) => {
        //empty means new
        const editContact = contact
            ? { ...contact }
            : {
                  Name: '',
                  Role: [],
                  Phones: [],
                  Email: '',
                  AddressLine1: '',
                  AddressLine2: '',
                  Suburb: '',
                  Postcode: '',
                  Country: ''
              };
        this.setState({ editContact });
    };
}

const styles = ({ palette, spacing }) => ({
    noColumn: { margin: `-${spacing(3)} ${spacing(1)} ${spacing(3)} ${spacing(1)}` },
    alignWithLabel: {
        marginTop: 10
    },

    iconContainer: {
        textAlign: 'center'
        // padding: '10px 0'
    },
    dataContainer: {
        textAlign: 'right'
        // padding: '10px 0'
    },
    uploadButtonContainer: {
        display: 'flex',
        width: '100%',
        justifyContent: 'center'
    },
    green: {
        color: palette.validation.good
    },
    warning: {
        color: palette.validation.suggested
    },
    error: {
        color: palette.validation.error
    }
});

export default compose(withRouter, withStyles(styles))(Certification);
